import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import api, { getCurrentTenantId } from '../services/api';

function getErrorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

interface Props<T extends { id: number }> {
  titulo: string;
  endpoint: string;
  colunas: { chave: keyof T; label: string; render?: (v: T) => ReactNode }[];
  renderFormulario: (item: Partial<T>, onChange: (campo: keyof T, valor: any) => void) => ReactNode;
  itemVazio: Partial<T>;
  validateItem?: (item: Partial<T>) => Promise<string | null> | string | null;
  headerActions?: ReactNode;
  /** Formulários com muitos campos (várias seções/colunas) ficam apertados no modal padrão. */
  modalLargo?: boolean;
  /**
   * Quando true, ao abrir a edição busca o registro completo em GET {endpoint}/{id}.
   * Útil para telas cujas colunas pesadas (ex.: imagens) vêm ocultas na listagem
   * (select:false no backend) e só devem ser carregadas no registro individual.
   */
  recarregarAoEditar?: boolean;
  /**
   * Rótulo do modal, quando "Novo — X" / "Editar — X" não descreve bem a tela.
   * Ex.: o cadastro da criança é o mesmo formulário nas duas situações, então
   * usa "Cadastro — Crianças" em ambas.
   */
  tituloModal?: string;
  /**
   * Chamado após criar/editar/excluir com sucesso. Serve para a página redesenhar
   * o que depende da lista — na tela de Salas, a capacidade de atendimento da
   * creche, que precisa crescer na hora em que a sala nova entra.
   */
  onDadosAlterados?: () => void;
}

export default function TabelaCrud<T extends { id: number }>({
  titulo, endpoint, colunas, renderFormulario, itemVazio, validateItem, headerActions, modalLargo, recarregarAoEditar,
  tituloModal, onDadosAlterados,
}: Props<T>) {
  const [itens, setItens] = useState<T[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<Partial<T> | null>(null);
  const [criando, setCriando] = useState(false);
  const [novoItem, setNovoItem] = useState<Partial<T>>(itemVazio);
  const [erro, setErro] = useState('');

  function withTenant(item: Partial<T>) {
    const tenantId = getCurrentTenantId();
    if (!tenantId || endpoint === '/creches') return item;
    return { ...item, id_creche: tenantId } as Partial<T>;
  }

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get<T[]>(endpoint);
      setItens(data);
    } catch (error) {
      // Prefere a mensagem real (ex.: sessão trocada em outra aba, creche errada)
      // — o texto genérico só entra quando não há nada melhor a dizer.
      setErro(getErrorMessage(error, 'Erro ao carregar dados. Verifique se o backend está rodando.'));
    } finally {
      setCarregando(false);
    }
  }, [endpoint]);

  useEffect(() => { carregar(); }, [carregar]);


  async function salvarNovo() {
    try {
      const payload = withTenant(novoItem);
      if (validateItem) {
        const validationError = await validateItem(payload);
        if (validationError) {
          setErro(validationError);
          return;
        }
      }

      await api.post(endpoint, payload);
      setNovoItem(itemVazio);
      setCriando(false);
      setErro('');
      carregar();
      onDadosAlterados?.();
    } catch (error) {
      setErro(getErrorMessage(error, 'Erro ao criar registro.'));
    }
  }

  async function salvarEdicao() {
    if (!editando?.id) return;
    try {
      const payload = withTenant(editando);
      if (validateItem) {
        const validationError = await validateItem(payload);
        if (validationError) {
          setErro(validationError);
          return;
        }
      }

      await api.put(`${endpoint}/${editando.id}`, payload);
      setEditando(null);
      setErro('');
      carregar();
      onDadosAlterados?.();
    } catch (error) {
      setErro(getErrorMessage(error, 'Erro ao atualizar registro.'));
    }
  }

  async function abrirEdicao(item: T) {
    // Abre já com os dados da linha (resposta imediata)...
    setEditando({ ...item });
    if (!recarregarAoEditar) return;
    // ...e, se pedido, mescla o registro completo (campos ocultos na lista).
    try {
      const { data } = await api.get<T>(`${endpoint}/${(item as any).id}`);
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setEditando((atual) =>
          atual && (atual as any).id === (data as any).id ? { ...atual, ...data } : atual,
        );
      }
    } catch {
      // Sem GET /:id ou falha — mantém os dados da linha.
    }
  }

  async function excluir(id: number) {
    if (!window.confirm('Confirma a exclusão?')) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      carregar();
      onDadosAlterados?.();
    } catch (error) {
      setErro(getErrorMessage(error, 'Erro ao excluir registro.'));
    }
  }

  function mudarCampo(setter: (prev: any) => any, campo: keyof T, valor: any) {
    setErro('');
    setter((prev: any) => ({ ...prev, [campo]: valor }));
  }

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">{titulo}</h2>
        <div className="header-acoes" style={{ display: 'flex', gap: '0.5rem' }}>
          {headerActions}
          <button className="btn-primario" onClick={() => { setCriando(true); setNovoItem(itemVazio); }}>
            + Novo
          </button>
        </div>
      </div>

      {erro && <div className="alerta alerta-erro">{erro} <button onClick={() => setErro('')}>✕</button></div>}

      {criando && (
        <div className="modal-overlay">
          <div className={`modal${modalLargo ? ' modal-largo' : ''}`}>
            <h3>{tituloModal || `Novo — ${titulo}`}</h3>
            <div className="form-grid">
              {renderFormulario(novoItem, (c, v) => mudarCampo(setNovoItem, c, v))}
            </div>
            {erro && <div className="alerta alerta-erro" style={{ marginTop: '0.75rem' }}>{erro} <button onClick={() => setErro('')}>✕</button></div>}
            <div className="modal-acoes">
              <button className="btn-primario" onClick={salvarNovo}>Salvar</button>
              <button className="btn-secundario" onClick={() => setCriando(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {editando && (
        <div className="modal-overlay">
          <div className={`modal${modalLargo ? ' modal-largo' : ''}`}>
            <h3>{tituloModal || `Editar — ${titulo}`}</h3>
            <div className="form-grid">
              {renderFormulario(editando, (c, v) => mudarCampo(setEditando, c, v))}
            </div>
            {erro && <div className="alerta alerta-erro" style={{ marginTop: '0.75rem' }}>{erro} <button onClick={() => setErro('')}>✕</button></div>}
            <div className="modal-acoes">
              <button className="btn-primario" onClick={salvarEdicao}>Salvar</button>
              <button className="btn-secundario" onClick={() => setEditando(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {carregando ? (
        <p className="carregando">Carregando…</p>
      ) : itens.length === 0 ? (
        <div className="vazio">
          <p>Nenhum registro encontrado.</p>
        </div>
      ) : (
        <div className="tabela-container">
          <table className="tabela">
            <thead>
              <tr>
                {colunas.map((c) => <th key={String(c.chave)}>{c.label}</th>)}
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item) => (
                <tr key={item.id}>
                  {colunas.map((c) => (
                    <td key={String(c.chave)}>
                      {c.render ? c.render(item) : String((item as any)[c.chave] ?? '–')}
                    </td>
                  ))}
                  <td className="acoes-coluna">
                    <button className="btn-acao" onClick={() => abrirEdicao(item)}>✏️</button>
                    <button className="btn-acao btn-perigo" onClick={() => excluir(item.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
